import { ArrowRightIcon } from '@heroicons/react/24/outline'
import type { ChartData, TooltipItem } from 'chart.js'
import clsx from 'clsx'
import { useMemo } from 'react'
import { Doughnut } from 'react-chartjs-2'

import { InlineLoader } from '../../../common/components'
import { getExternalTooltipOptions } from '../../../common/components/Charts/externalTooltip'
import { Button, Divider, Text } from '../../../common/designs'
import { useDashboard, useEnhancedNavigate } from '../../../common/hooks'
import type { AdminMainPathEnum } from '../../../common/types/routing'

const DOUGHNUT_SEGMENT_FILLS = [
  'hsla(243, 76%, 58%, 1)',
  'hsla(262, 72%, 56%, 1)',
  'hsla(217, 82%, 58%, 1)',
  'hsla(199, 78%, 48%, 1)',
  'hsla(280, 52%, 58%, 1)',
  'hsla(231, 70%, 62%, 1)',
]

const CHART_SURFACE = '#ffffff'
const ONE_DECIMAL_PRECISION = 10

const formatTooltipNumber = (value: number): string => {
  const rounded = Math.round(value * ONE_DECIMAL_PRECISION) / ONE_DECIMAL_PRECISION
  return rounded.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 })
}

const enrichDoughnutData = (data: ChartData<'doughnut'>): ChartData<'doughnut'> => {
  const dataset = data.datasets[0]
  if (!dataset) return data

  const rawData = dataset.data as number[]
  const sliceCount = rawData.length

  let backgroundColor = dataset.backgroundColor
  if (typeof backgroundColor === 'string') {
    backgroundColor = Array.from(
      { length: sliceCount },
      (_, i) => DOUGHNUT_SEGMENT_FILLS[i % DOUGHNUT_SEGMENT_FILLS.length],
    )
  }

  const borderWidth =
    typeof dataset.borderWidth === 'number'
      ? dataset.borderWidth
      : Array.from({ length: sliceCount }, () => 2)

  let borderColor = dataset.borderColor
  if (borderColor === undefined || typeof borderColor === 'string') {
    borderColor = Array.from({ length: sliceCount }, () => CHART_SURFACE)
  }

  return {
    ...data,
    datasets: [
      {
        ...dataset,
        backgroundColor,
        borderWidth,
        borderColor,
        hoverBorderColor: CHART_SURFACE,
        hoverOffset: 6,
      },
    ],
  }
}

interface Props {
  chartData: ChartData<'doughnut'> | undefined
  titleProps: {
    label: string
    value: string | number | undefined
  }
  descriptionProps: {
    label: string
    value: string | number | undefined
  }
  navProps: {
    label: string
    link: AdminMainPathEnum | string
  }
  isLoading?: boolean
}

const StatusCard = (props: Props) => {
  const { titleProps, descriptionProps, chartData, navProps, isLoading = false } = props
  const { enhancedNavigate: navigate } = useEnhancedNavigate()
  const { user } = useDashboard()

  const displayChartData = useMemo(
    () => (chartData ? enrichDoughnutData(chartData) : undefined),
    [chartData],
  )

  const doughnutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      animation: {
        animateRotate: true,
        animateScale: true,
      },
      plugins: {
        legend: { display: false },
        tooltip: getExternalTooltipOptions<'doughnut'>((ctx: TooltipItem<'doughnut'>) => {
          if (typeof ctx.parsed === 'number') return formatTooltipNumber(ctx.parsed)
          return `${ctx.parsed}`
        }),
      },
      cutout: '66%',
    }),
    [],
  )

  return (
    <div
      className={clsx(
        'flex w-72 flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-gradient-to-b from-white via-white to-slate-50/95 shadow-sm ring-1 ring-slate-900/[0.04] transition hover:shadow-md',
        isLoading && 'animate-pulse',
      )}
    >
      <div className='w-full border-b border-slate-100/90 bg-white/80 px-4 py-3'>
        <div className='mb-2 flex justify-between gap-2'>
          {isLoading ? (
            <div className='mb-2 h-2.5 w-full rounded-full bg-gray-200'></div>
          ) : (
            <>
              <Text variant='h6' size='m' weight={600} className='text-slate-800'>
                {titleProps.label}
              </Text>

              <Text variant='h6' size='m' weight={700} color='primary'>
                {titleProps.value}
              </Text>
            </>
          )}
        </div>

        <div className='flex justify-between gap-2'>
          {isLoading ? (
            <div className='h-2.5 w-full rounded-full bg-gray-200'></div>
          ) : (
            <>
              <Text variant='h6' size='s' weight={500} color='lightGray'>
                {descriptionProps.label}
              </Text>

              <Text variant='h6' size='s' weight={600} color='gray'>
                {descriptionProps.value}
              </Text>
            </>
          )}
        </div>
      </div>

      <Divider />

      <div className='relative flex min-h-52 flex-col items-center justify-center px-3 pb-2 pt-4'>
        {!isLoading && displayChartData ? (
          <>
            <div className='relative mx-auto size-44 max-w-[min(100%,11rem)]'>
              <Doughnut options={doughnutOptions} data={displayChartData} />
              <div className='pointer-events-none absolute inset-0 flex items-center justify-center p-2'>
                <div className='flex max-w-[min(100%,6.75rem)] flex-col items-center justify-center gap-0.5 text-center'>
                  <Text
                    variant='span'
                    size='2xl'
                    weight={800}
                    className='max-w-full truncate tabular-nums leading-none text-slate-900'
                  >
                    {titleProps.value}
                  </Text>
                  <Text
                    variant='span'
                    size='xs'
                    weight={600}
                    color='lightGray'
                    className='max-w-full hyphens-auto break-words uppercase leading-snug tracking-[0.08em]'
                  >
                    {titleProps.label}
                  </Text>
                </div>
              </div>
            </div>
          </>
        ) : (
          <InlineLoader size='2xl' />
        )}
      </div>

      {user.isAdmin && (
        <div className='border-t border-slate-100 bg-slate-50/80 px-2 py-3'>
          <Button
            variant='ghost'
            size='s'
            className='w-full justify-center text-primary-700 hover:bg-primary-50'
            iconEnd={<ArrowRightIcon className='size-4' />}
            onClick={() => navigate(navProps.link)}
          >
            {navProps.label}
          </Button>
        </div>
      )}
    </div>
  )
}

export default StatusCard

